import React, { useEffect, useMemo, useState } from 'react'
import { Card, Box, Chip, Typography, CircularProgress } from '@mui/material'
import { blue, red } from '../../../../colors'
import Dropdown from '../../../../components/Dropdown'
import CustomPagination from '../../../../components/CustomTable/CustomPagination'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FlaggingAndSummarizationManager } from '../../../../APIManager'
import { notification } from '../../../../components/Notifications'
import { type ReportWithComments } from '../../../../models/Report'
import { useParams, useSearchParams } from 'react-router-dom'
import { FlaggedCommentType } from '../constants'
import { getFlaggedCommentErrorMessage } from '../helper'
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded'
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded'
import GenericButton from '../../../../components/GenericButton'
import CustomTooltip from '../../../../components/CustomTooltip'

interface CommentListProps {
  results: string[]
  showFlagged: boolean
  report: ReportWithComments
  disableFlaggedCommentActions?: boolean
}

export interface FlaggedComment {
  commentText: string
  distributionSeriesId: string
  flagType: string
  isGlobal: boolean
  itemId: string
  itemRevision: number
  sectionId: string
  termId: string
}

const COMMENTS_PER_PAGE = 10
const FLAGGED_COMMENT_ACTIONS_DISABLED_TOOLTIP = 'Instructor report comments are aggregated across course sections. Save or flag comments from the course section report.'

const CommentList: React.FC<CommentListProps> = ({
  results,
  showFlagged,
  report,
  disableFlaggedCommentActions = false
}) => {
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [updatingCommentText, setUpdatingCommentText] = useState<string | null>(null)
  const projectId = searchParams.get('projectId')
  const termId = searchParams.get('termId')
  const { id: sectionId } = useParams()
  const queryClient = useQueryClient()

  const getUpdateCommentMessage = (flagType: string): string => {
    switch (flagType) {
      case FlaggedCommentType.FLAGGED:
        return 'Flagged as harmful or inappropriate'
      case FlaggedCommentType.STARRED:
        return 'Comment Successfully Saved'
      default:
        return ''
    }
  }

  const { data: flaggedComments, isLoading: flaggedCommentsLoading, isFetching: flaggedCommentsFetching } = useQuery<any>({
    queryKey: ['getFlaggedCommentsList', sectionId, termId, projectId],
    queryFn: async () => {
      const res = await FlaggingAndSummarizationManager.getFlaggedComments({
        termId: termId ?? '',
        distributionSeriesId: projectId ?? '',
        sectionId: sectionId ?? ''
      })
      return res.data
    },
    enabled: !!sectionId && !disableFlaggedCommentActions
  })

  const { mutate: deleteFlaggedComment, isPending: deleteFlaggedCommentLoading } = useMutation<any, unknown, { commentId: string, isStarred?: boolean }>({
    mutationFn: async ({ commentId, isStarred }) => {
      const response = await FlaggingAndSummarizationManager.deleteFlaggedComment(commentId)
      return response.data
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['getFlaggedCommentsList', sectionId, termId, projectId] })
      notification(variables.isStarred ? 'Comment Successfully unsaved' : 'Resolved as not harmful', { variant: 'success' })
    },
    onError: (error: any) => {
      notification(`Failed to delete flagged comment${error?.message ? `: ${error.message}` : ''}`, { variant: 'error' })
    },
    meta: { silent: true }
  })

  const { mutate: updateFlaggedComment, isPending: updateFlaggedCommentsLoading } = useMutation<any, unknown, FlaggedComment>({
    mutationFn: async (data) => {
      const response = await FlaggingAndSummarizationManager.updateFlaggedComments(data)
      return response.data
    },
    onSuccess: (_, variables) => {
      const { flagType } = variables
      void queryClient.invalidateQueries({ queryKey: ['getFlaggedCommentsList', sectionId, termId, projectId] })
      notification(getUpdateCommentMessage(flagType), { variant: 'success' })
    },
    onError: (error: any) => {
      notification(getFlaggedCommentErrorMessage(error), { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleUpdateFlaggedComment = (comment: string, flagType: FlaggedCommentType): void => {
    if (disableFlaggedCommentActions) return
    setUpdatingCommentText(comment)
    const data: FlaggedComment = {
      commentText: comment,
      distributionSeriesId: projectId ?? '',
      flagType,
      isGlobal: true,
      itemId: report.itemId ?? '',
      itemRevision: report.revision,
      sectionId: sectionId ?? '',
      termId: termId ?? ''
    }
    updateFlaggedComment(data)
  }

  const checkFlaggedComment = (commentText: string): { commentText: string, flagType: string, isGlobal: boolean } | undefined => {
    const flagged = flaggedComments?.find((flagged: FlaggedComment) => flagged.commentText === commentText && flagged.flagType === FlaggedCommentType.FLAGGED)
    return flagged
      ? { commentText: flagged.commentText, flagType: flagged.flagType, isGlobal: flagged.isGlobal }
      : undefined
  }
  const checkIsStarredComment = (commentText: string): { commentText: string, flagType: string, isGlobal: boolean } | undefined => {
    const flagged = flaggedComments?.find((flagged: FlaggedComment) => flagged.commentText === commentText && flagged.flagType === FlaggedCommentType.STARRED)
    return flagged
      ? { commentText: flagged.commentText, flagType: flagged.flagType, isGlobal: flagged.isGlobal }
      : undefined
  }

  const visibleResults = useMemo(() => {
    if (showFlagged) return results
    return results.filter((r) => checkFlaggedComment(r) === undefined)
  }, [results, showFlagged, flaggedComments])

  const totalResults = visibleResults.length
  const numPages = Math.ceil(Math.max(totalResults, 1) / COMMENTS_PER_PAGE)
  const paginatedResults = visibleResults.slice((page - 1) * COMMENTS_PER_PAGE, page * COMMENTS_PER_PAGE)

  const handlePageChange = (newPage: number): void => { setPage(newPage) }

  const handleResolveNotHarmful = (commentText: string, isStarred?: boolean): void => {
    if (disableFlaggedCommentActions) return
    setUpdatingCommentText(commentText)
    const flagged = flaggedComments?.find((c: FlaggedComment) => c.commentText === commentText)
    if (flagged?.flaggedCommentId) {
      deleteFlaggedComment({ commentId: flagged.flaggedCommentId, isStarred })
    }
  }

  const isCommentMutationLoading = (commentText: string): boolean => {
    return (updateFlaggedCommentsLoading || deleteFlaggedCommentLoading) && updatingCommentText === commentText
  }

  const isCommentActionDisabled = (commentText: string): boolean => {
    return disableFlaggedCommentActions || isCommentMutationLoading(commentText)
  }

  const handleStaredComment = (isStarred: boolean, comment: string): void => {
    if (isStarred) {
      handleResolveNotHarmful(comment, isStarred)
    } else {
      handleUpdateFlaggedComment(comment, FlaggedCommentType.STARRED)
    }
  }

  const isAnyLoading = flaggedCommentsLoading

  const renderLoadingIndicator = (commentText: string): JSX.Element | null => {
    if ((updateFlaggedCommentsLoading || deleteFlaggedCommentLoading || flaggedCommentsFetching) && updatingCommentText === commentText) {
      return <CircularProgress size={16} />
    }
    return null
  }

  useEffect(() => {
    if (!flaggedCommentsLoading && !flaggedCommentsFetching) {
      setUpdatingCommentText(null)
    }
  }, [flaggedCommentsLoading, flaggedCommentsFetching])

  if (isAnyLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {paginatedResults.map((comment, index) => {
        const commentFlagged = checkFlaggedComment(comment)
        const starredComment = checkIsStarredComment(comment)
        const saveCommentLabel = starredComment?.flagType === FlaggedCommentType.STARRED ? 'Unsave comment' : 'Save comment'
        const actionTooltip = disableFlaggedCommentActions ? FLAGGED_COMMENT_ACTIONS_DISABLED_TOOLTIP : saveCommentLabel
        return (
            <Card key={index} sx={{ mb: 3, position: 'relative' }}>
              <Box sx={{ p: 1, pl: 3 }} display={'flex'} justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary" maxWidth={'70%'}>
                  {comment}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {renderLoadingIndicator(comment)}
                  {commentFlagged && (
                    <Chip
                      sx={{
                        color: red[700],
                        borderRadius: '6px',
                        border: `1px solid ${red[150]}`,
                        backgroundColor: red[50],
                        fontSize: '12px',
                        fontWeight: 700,
                        padding: 0
                      }}
                      size="small"
                      label={`Offensive Language${commentFlagged?.isGlobal ? ' (AI)' : ''}`}
                    />
                  )}
                  <CustomTooltip title={actionTooltip}>
                    <GenericButton
                      icon={starredComment?.flagType === FlaggedCommentType.STARRED ? <BookmarkRoundedIcon sx={{ color: blue[500] }} /> : <BookmarkBorderRoundedIcon />}
                      buttonType={ButtonType.Ghost}
                      disabled={isCommentActionDisabled(comment)}
                      aria-label={saveCommentLabel}
                      title={saveCommentLabel}
                      onClick={() => { handleStaredComment(starredComment?.flagType === FlaggedCommentType.STARRED, comment) }}
                    />
                  </CustomTooltip>
                  <Dropdown
                    dropdownType='action'
                    menuItems={[
                      {
                        text: 'Flag as harmful or inappropriate.',
                        action: () => { handleUpdateFlaggedComment(comment, FlaggedCommentType.FLAGGED) },
                        visible: !commentFlagged,
                        disabled: disableFlaggedCommentActions,
                        tooltipTitle: disableFlaggedCommentActions ? FLAGGED_COMMENT_ACTIONS_DISABLED_TOOLTIP : undefined
                      },
                      {
                        text: 'Resolve as not harmful',
                        action: () => {
                          handleResolveNotHarmful(comment)
                        },
                        visible: !!commentFlagged,
                        disabled: disableFlaggedCommentActions,
                        tooltipTitle: disableFlaggedCommentActions ? FLAGGED_COMMENT_ACTIONS_DISABLED_TOOLTIP : undefined
                      }
                    ]}
                    extraButtonProps={{
                      buttonType: ButtonType.Ghost,
                      style: { padding: 0 },
                      disabled: isCommentMutationLoading(comment)
                    }}
                  />
                </Box>
              </Box>
            </Card>
        )
      })}

      { numPages > 1 && (
        <Box mt={2}>
          <CustomPagination
            totalResults={totalResults}
            numPages={numPages}
            pageNumber={page}
            pageSize={COMMENTS_PER_PAGE}
            handlePageChange={handlePageChange}
            showPageSizeOptions={false}
          />
        </Box>
      )}
    </>
  )
}

export default CommentList
